import { HeaderType, HeaderObject, root } from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';

export const AUTHORIZATION_HEADER_NAME = 'AuthorizationToken';
export const BUSINESS_UNIT_HEADER_NAME = 'Business-Unit';

export interface IError {
    status: number;
}
export interface IDataMapperOperations {
    [key: string]: Function | undefined;
    request?(request: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [k: string]: any;
    }): {};
    response?(response: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [k: string]: any;
    }): {};
}
export interface IDataMappings {
    [key: string]: IDataMapperOperations;
}
export interface IResponse<T> {
    data: T;
    reponseHeaders: Headers | {};
}
export interface IPayload {
    url: string;
    body?: {};
    options?: {};
    headers?: {};
    returnAsPlainText?: boolean;
}
export interface IGetPayload {
    url: string;
    options?: {};
    headers?: {};
    params?: {};
}
export interface IRequestOptions {
    debounceInterval?: number;
    bypassCache?: boolean;
}
export interface IHttpTransport {
    addGlobalHeaders(header: HeaderObject): void;
    removeGlobalHeader(headerName: string): void;
    delete<T>(payload: string | IPayload): Promise<IResponse<T>>;
    post<T>(payload: IPayload): Promise<IResponse<T>>;
    patch<T>(payload: IPayload): Promise<IResponse<T>>;
    put<T>(payload: IPayload): Promise<IResponse<T>>;
    get<T>(payload: string | IGetPayload, IRequestOptions?: IRequestOptions): Promise<IResponse<T>>;
}
export interface IDataMapper {
    // eslint-disable-next-line @typescript-eslint/ban-types
    mapRequest(name: string, request: Object): {};
    // eslint-disable-next-line @typescript-eslint/ban-types
    mapResponse(name: string, response: Object): {};
    dataMappings?: IDataMappings;
}

export interface IConfig {
    cartId?: string;
    httpTransport: IHttpTransport;
    dataMapper?: IDataMapper;
    businessUnitId?: string;
    drupalDomain?: string;
}

export class Api {
    protected bearerToken: string;
    protected httpTransport: IHttpTransport;
    protected businessUnitId: string | undefined;
    private logger: ELCLogger;

    constructor(configStore: IConfig, serviceName: string, serviceVersion: string) {
        this.httpTransport = configStore.httpTransport;
        this.businessUnitId = configStore.businessUnitId;
        this.logger = new ELCLogger({ serviceName, serviceVersion });
    }

    public addHeader(header: HeaderObject) {
        this.httpTransport.addGlobalHeaders(header);
    }

    public removeHeader(headerName: HeaderType) {
        this.httpTransport.removeGlobalHeader(headerName);
    }

    public setBearerToken(bearerToken: string) {
        if (bearerToken) {
            this.addHeader({
                [AUTHORIZATION_HEADER_NAME]: `${bearerToken}`
            });
        } else {
            this.removeHeader(AUTHORIZATION_HEADER_NAME);
        }
    }
    protected delete = async <T>(payload: IPayload) => {
        let response: IResponse<T> = { data: {} as T, reponseHeaders: {} };

        try {
            response = await this.httpTransport.delete<T>(payload);
        } catch (e) {
            this.handleError(e, 'delete', payload);
        }

        return response;
    };

    protected get = async <T>(
        payload: IGetPayload | string,
        { debounceInterval = 200, bypassCache = false }: IRequestOptions = {}
    ) => {
        let response: IResponse<T> = { data: {} as T, reponseHeaders: {} };

        try {
            response = await this.httpTransport.get<T>(payload, {
                debounceInterval,
                bypassCache
            });
        } catch (e) {
            this.handleError(e, 'get', payload);
        }

        return response;
    };

    protected patch = async <T>(payload: IPayload) => {
        let response: IResponse<T> = { data: {} as T, reponseHeaders: {} };

        try {
            response = await this.httpTransport.patch<T>(payload);
        } catch (e) {
            this.handleError(e, 'patch', payload);
        }

        return response;
    };

    protected post = async <T>(payload: IPayload) => {
        let response: IResponse<T> = { data: {} as T, reponseHeaders: {} };
        try {
            response = await this.httpTransport.post<T>(payload);
        } catch (e) {
            this.handleError(e, 'post', payload);
        }

        return response;
    };
    protected put = async <T>(payload: IPayload) => {
        let response: IResponse<T> = { data: {} as T, reponseHeaders: {} };

        try {
            response = await this.httpTransport.put<T>(payload);
        } catch (e) {
            this.handleError(e, 'put', payload);
        }

        return response;
    };

    private handleError = (e: Error, requestType: string, originalPayload: IPayload | string) => {
        this.logger.error({
            triggerType: TriggerType.api,
            message: `${e}`,
            payload: {
                context: {
                    subdomain: root.site && root.site.client && root.site.client.subdomain,
                    originalPayload,
                    requestType
                },
                error: e
            }
        });
    };
}
